import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Partner } from '../types/partners';
import { fetchVoteSummary, isVotesApiConfigured, PartnerApiUnauthorizedError, setPartnerVote } from '../utils/partnerApi';
import { trackEvent } from '../utils/analytics';
import { SignInPrompt } from '../components/partners/SignInPrompt';
export type VotesStatus = 'loading' | 'ready' | 'unavailable' | 'error';
type PartnerVotesValue = {
  status: VotesStatus;
  authenticated: boolean;
  counts: Record<string, number>;
  isVoted: (slug: string) => boolean;
  isPending: (slug: string) => boolean;
  errorFor: (slug: string) => string | null;
  toggleVote: (partner: Partner, placement: string) => void;
};
const PartnerVotesContext = createContext<PartnerVotesValue | null>(null);

/** Real, server-persisted partner votes. Nothing is counted locally. */
export function PartnerVotesProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [status, setStatus] = useState<VotesStatus>(isVotesApiConfigured() ? 'loading' : 'unavailable');
  const [authenticated, setAuthenticated] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [voted, setVoted] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [promptPartner, setPromptPartner] = useState<Partner | null>(null);
  useEffect(() => {
    if (!isVotesApiConfigured()) return;
    let cancelled = false;
    fetchVoteSummary().then(summary => {
      if (cancelled) return;
      setCounts(summary.counts ?? {});
      setVoted(new Set(summary.viewerVotes ?? []));
      setAuthenticated(Boolean(summary.authenticated));
      setStatus('ready');
    }).catch(() => !cancelled && setStatus('error'));
    return () => {
      cancelled = true;
    };
  }, []);
  const toggleVote = useCallback((partner: Partner, placement: string) => {
    const {
      slug
    } = partner;
    if (status !== 'ready' || !authenticated) {
      setPromptPartner(partner);
      return;
    }
    if (pending.has(slug)) return;
    const nextVoted = !voted.has(slug);
    setPending(current => new Set(current).add(slug));
    setErrors(({
      [slug]: _removed,
      ...rest
    }) => rest);
    setPartnerVote(slug, nextVoted).then(result => {
      setCounts(current => ({
        ...current,
        [slug]: result.count
      }));
      setVoted(current => {
        const next = new Set(current);
        if (result.voted) next.add(slug);else next.delete(slug);
        return next;
      });
      trackEvent('partner_upvote', {
        partner_id: partner.id,
        partner_name: partner.name,
        vote_action: result.voted ? 'add' : 'remove',
        placement
      });
    }).catch(error => {
      if (error instanceof PartnerApiUnauthorizedError) {
        setAuthenticated(false);
        setPromptPartner(partner);
      } else {
        setErrors(current => ({
          ...current,
          [slug]: 'Your vote couldn’t be saved. Please try again.'
        }));
      }
    }).finally(() => setPending(current => {
      const next = new Set(current);
      next.delete(slug);
      return next;
    }));
  }, [status, authenticated, pending, voted]);
  const value = useMemo<PartnerVotesValue>(() => ({
    status,
    authenticated,
    counts,
    isVoted: slug => voted.has(slug),
    isPending: slug => pending.has(slug),
    errorFor: slug => errors[slug] ?? null,
    toggleVote
  }), [status, authenticated, counts, voted, pending, errors, toggleVote]);
  const closePrompt = useCallback(() => setPromptPartner(null), []);
  return <PartnerVotesContext.Provider value={value} data-id="element-1332" data-mp="21lua2d69yaez">
      {children}
      <SignInPrompt partner={promptPartner} votingOpen={status === 'ready'} onClose={closePrompt} data-id="element-1333" data-mp="26k5m8uk1kx0d" />
    </PartnerVotesContext.Provider>;
}
export function usePartnerVotes() {
  const context = useContext(PartnerVotesContext);
  if (!context) throw new Error('usePartnerVotes must be used inside PartnerVotesProvider');
  return context;
}